import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { useAlert } from 'dashboard/composables';

const DEFAULT_WINDOW_SECONDS = 30;

// Oferta de desfazer mais recente: o atalho de teclado sempre desfaz a última exclusão.
let pendingUndo = null;

export const isEditableTarget = target => {
  if (!target || typeof target.closest !== 'function') return false;
  return Boolean(
    target.closest(
      'input, textarea, select, [contenteditable=""], [contenteditable="true"]'
    )
  );
};

export const isUndoShortcut = event =>
  (event.ctrlKey || event.metaKey) &&
  !event.shiftKey &&
  !event.altKey &&
  String(event.key || '').toLowerCase() === 'z';

export const undoShortcutLabel = () => {
  const platform =
    globalThis.navigator?.userAgentData?.platform ||
    globalThis.navigator?.platform ||
    globalThis.navigator?.userAgent ||
    '';
  return /mac|iphone|ipad/i.test(platform) ? '⌘Z' : 'Ctrl+Z';
};

const onKeydown = event => {
  if (!pendingUndo || event.defaultPrevented || !isUndoShortcut(event)) return;
  // Em campos de texto o Ctrl/Cmd+Z continua desfazendo a digitação.
  if (isEditableTarget(event.target)) return;

  event.preventDefault();
  pendingUndo.undo();
};

const clearPendingUndo = toastId => {
  if (!pendingUndo || (toastId && pendingUndo.toastId !== toastId)) return;
  window.clearTimeout(pendingUndo.timer);
  window.removeEventListener('keydown', onKeydown);
  pendingUndo = null;
};

// Mostra o aviso com "Desfazer" e registra o atalho Ctrl/Cmd+Z para a oferta mais recente.
const showUndoOffer = ({
  toastKey,
  message,
  windowSeconds,
  elapsedMs,
  t,
  run,
}) => {
  const durationMs = Math.max(1000, windowSeconds * 1000 - elapsedMs);
  const toastId = `conversation-delete-undo-${toastKey}-${Date.now()}`;
  let used = false;

  const undo = async () => {
    if (used) return;
    used = true;
    emitter.emit('dismissToastMessage', toastId);
    clearPendingUndo(toastId);
    await run();
  };

  clearPendingUndo();
  useAlert(message, {
    id: toastId,
    type: 'button',
    variant: 'info',
    message: t('CONVERSATION.UNDO_DELETE_CONVERSATION'),
    hint: t('CONVERSATION.UNDO_DELETE_CONVERSATION_SHORTCUT', {
      shortcut: undoShortcutLabel(),
    }),
    onClick: undo,
    duration: durationMs,
    pauseOnHover: false,
  });

  pendingUndo = {
    toastId,
    undo,
    timer: window.setTimeout(() => clearPendingUndo(toastId), durationMs),
  };
  window.addEventListener('keydown', onKeydown);
};

export function useConversationDeleteUndo() {
  const store = useStore();
  const { t } = useI18n();

  const offerUndo = ({
    conversationId,
    undoToken,
    windowSeconds = DEFAULT_WINDOW_SECONDS,
    elapsedMs = 0,
  }) => {
    if (!undoToken) {
      useAlert(t('CONVERSATION.SUCCESS_DELETE_CONVERSATION'));
      return;
    }

    showUndoOffer({
      toastKey: conversationId,
      message: t('CONVERSATION.SUCCESS_DELETE_CONVERSATION_UNDO', {
        seconds: windowSeconds,
      }),
      windowSeconds,
      elapsedMs,
      t,
      run: async () => {
        try {
          await store.dispatch('undoDeleteConversation', {
            conversationId,
            undoToken,
          });
          useAlert(
            t('CONVERSATION.SUCCESS_UNDO_DELETE_CONVERSATION'),
            'success'
          );
        } catch {
          useAlert(t('CONVERSATION.FAIL_UNDO_DELETE_CONVERSATION'), 'error');
        }
      },
    });
  };

  // Lote: um único "Desfazer" restaura todas as conversas ainda pendentes.
  const offerBulkUndo = ({
    conversations = [],
    windowSeconds = DEFAULT_WINDOW_SECONDS,
    elapsedMs = 0,
  }) => {
    const count = conversations.length;
    if (!count) return;

    showUndoOffer({
      toastKey: `bulk-${count}`,
      message: t(
        'BULK_ACTION.DELETE.SUCCESS_UNDO',
        { count, seconds: windowSeconds },
        count
      ),
      windowSeconds,
      elapsedMs,
      t,
      run: async () => {
        try {
          const restored = await store.dispatch(
            'bulkUndoDeleteConversations',
            conversations
          );
          if (restored.length === count) {
            useAlert(t('BULK_ACTION.DELETE.UNDO_SUCCESS'), 'success');
          } else {
            useAlert(
              t('BULK_ACTION.DELETE.UNDO_PARTIAL', {
                restored: restored.length,
                total: count,
              }),
              'error'
            );
          }
        } catch {
          useAlert(t('CONVERSATION.FAIL_UNDO_DELETE_CONVERSATION'), 'error');
        }
      },
    });
  };

  return { offerUndo, offerBulkUndo };
}
